export interface PageSEO {
    title: string;
    description: string;
    keywords?: string;
    canonical?: string;
    ogImage?: string;
    noindex?: boolean;
}

const OG_DEFAULT = 'https://ortemtech.com/og-default.png';

// Per-service OG images (Unsplash, consistent with industry hero images already used on site)
const OG_MOBILE = 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&auto=format&fit=crop&q=80';
const OG_WEB = 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&auto=format&fit=crop&q=80';
const OG_AI = 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=1200&auto=format&fit=crop&q=80';
const OG_CLOUD = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&auto=format&fit=crop&q=80';
const OG_DATA = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80';
const OG_HEALTHCARE = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1200&auto=format&fit=crop&q=80';
const OG_FINTECH = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&auto=format&fit=crop&q=80';
const OG_LOGISTICS = 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1200&auto=format&fit=crop&q=80';
const OG_ECOMMERCE = 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&auto=format&fit=crop&q=80';
const OG_SECURITY = 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=1200&auto=format&fit=crop&q=80';

export const seoConfig: Record<string, PageSEO> = {
    // Home Page
    home: {
        title: "AI & Mobile App Development Company | Ortem Technologies",
        description: "Software & AI development company since 2012, serving clients worldwide. 1,000+ apps for startups & enterprises. 98% client retention. Free consultation.",
        keywords: "custom software development company, AI development services, web application development, mobile app development company, digital transformation partner, cloud solutions provider, IoT development services, enterprise software solutions",
        canonical: "https://ortemtech.com/",
        ogImage: OG_DEFAULT
    },

    // Reports / linkable assets
    costReport: {
        title: "2026 Custom Software Development Cost Report | Ortem",
        description: "What custom software actually costs in 2026: build cost vs fully-loaded investment, a feature-by-feature price list, and the model behind 300+ projects.",
        keywords: "software development cost 2026, app development cost, custom software pricing, saas development cost, cost to build an app, mvp cost",
        canonical: "https://ortemtech.com/reports/software-development-cost-2026/",
        ogImage: OG_DEFAULT
    },

    // Company Pages
    about: {
        title: "About Ortem Technologies | US-Managed Dev Since 2012",
        description: "Custom software & AI development company since 2012, serving 300+ clients worldwide. 200+ engineers, 98% client retention rate. Learn our story and approach.",
        keywords: "technology services company, software development company, digital transformation partner, web and mobile app experts, cloud and AI solutions provider",
        canonical: "https://ortemtech.com/about/",
        ogImage: OG_DEFAULT
    },
    careers: {
        title: "Careers at Ortem Technologies | Join Our Tech Team",
        description: "Join Ortem Technologies for AI, mobile & cloud engineering roles. Remote-first culture, competitive benefits, global projects. See open positions.",
        keywords: "tech jobs, software engineer careers, remote developer jobs, AI jobs, mobile app developer careers, Ortem Technologies careers",
        canonical: "https://ortemtech.com/careers/",
        ogImage: OG_DEFAULT
    },
    contact: {
        title: "Contact Ortem | Free Software Dev Consultation",
        description: "Contact Ortem Technologies for custom software, AI & mobile development. Free consultation within 24 hours. No commitment required.",
        keywords: "contact software company, hire developers, software consultation, app development quote, tech partner contact",
        canonical: "https://ortemtech.com/contact/",
        ogImage: OG_DEFAULT
    },

    // Blog
    blog: {
        title: "Tech Blog | AI & Software Insights | Ortem Technologies",
        description: "Expert perspectives on software development, AI, cloud computing, and digital transformation from the Ortem Technologies team.",
        keywords: "tech blog, software development insights, AI articles, mobile development tips, digital transformation blog",
        canonical: "https://ortemtech.com/blog/",
        ogImage: OG_DEFAULT
    },

    // Case Studies
    caseStudies: {
        title: 'Case Studies | 1,000+ Software Projects Delivered | Ortem',
        description: 'Real-world case studies from Ortem Technologies. 1,000+ projects across mobile, AI, web & cloud for clients in USA, UK & UAE. See the results.',
        keywords: 'software development case studies, app development portfolio, AI project results, mobile app success stories, client outcomes Ortem Technologies',
        canonical: 'https://ortemtech.com/case-studies/',
        ogImage: OG_DEFAULT
    },

    // Services Index Page
    services: {
        title: 'Software Development Services | Web, Mobile & AI | Ortem',
        description: 'US software development company. Custom software, web & mobile apps, AI, cloud & DevOps — 1,000+ projects delivered worldwide since 2012. Get a free quote.',
        keywords: 'software development services, custom software company, enterprise software development, web development services, mobile app development, AI development services, cloud and DevOps services, cybersecurity services',
        canonical: 'https://ortemtech.com/services/',
        ogImage: OG_DEFAULT
    },

    // Services Pages
    'mobile-app-development': {
        title: 'Mobile App Development Company USA | iOS & Android | Ortem',
        description: 'US mobile app development company. 300+ iOS, Android, Flutter & React Native apps. HIPAA-compliant. Launches in 8–14 weeks. Free strategy call.',
        keywords: 'mobile app development company USA, iOS app development company, Android app development company, Flutter app development USA, React Native development company, cross-platform mobile apps',
        canonical: 'https://ortemtech.com/services/mobile-app-development/',
        ogImage: OG_MOBILE
    },
    'web-development': {
        title: 'Web Development Company USA | React & Next.js | Ortem',
        description: 'Custom web development in the USA. React, Next.js & Node.js — SEO-optimized, fast-loading, conversion-focused web apps and platforms. Free quote.',
        keywords: 'web development company USA, custom web development services, React development company, Next.js development, Node.js web development, web application development USA',
        canonical: 'https://ortemtech.com/services/web-development/',
        ogImage: OG_WEB
    },
    'ai-ml-solutions': {
        title: 'AI Development Company USA | ML & Generative AI | Ortem',
        description: 'Leading US AI development company. Custom AI, ML, generative AI & computer vision for enterprises. Book a free AI consultation.',
        keywords: 'AI development company USA, machine learning development services, generative AI development, generative AI solutions, custom AI development, computer vision development, AI ML services USA',
        canonical: 'https://ortemtech.com/services/ai-ml-solutions/',
        ogImage: OG_AI
    },
    'ecommerce': {
        title: 'eCommerce Development USA | Shopify & Headless | Ortem',
        description: 'US eCommerce development team. Shopify Plus, headless commerce, B2B marketplaces & custom platforms. Scale your digital revenue. Free audit.',
        keywords: 'ecommerce development company USA, Shopify development company, headless commerce development, custom ecommerce platform, B2B ecommerce development, online store development USA',
        canonical: 'https://ortemtech.com/services/ecommerce/',
        ogImage: OG_ECOMMERCE
    },
    'cloud-devops': {
        title: 'DevOps Implementation & Cloud Services USA | Ortem',
        description: 'US DevOps & cloud services. AWS, Azure, GCP — CI/CD pipelines, Kubernetes, IaC, DevSecOps. Cut deploy time from weeks to hours. Free audit.',
        keywords: 'devops implementation services, devops consulting services, devops as a service, devops managed services, devops automation services, devops transformation, cloud devops services USA, ci cd pipeline services, kubernetes consulting, aws devops services, azure devops consulting',
        canonical: 'https://ortemtech.com/services/cloud-devops/',
        ogImage: OG_CLOUD
    },
    'iot': {
        title: 'IoT Development Company USA | Industrial IoT & Edge | Ortem',
        description: 'Expert US IoT development. Secure, scalable IoT solutions for industrial, healthcare & smart city applications. AWS & Azure IoT Hub expertise.',
        keywords: 'IoT development company USA, industrial IoT development, smart device app development, IoT cloud platform, edge computing IoT, connected device solutions USA',
        canonical: 'https://ortemtech.com/services/iot/',
        ogImage: OG_DEFAULT
    },
    'qa-testing': {
        title: 'QA & Software Testing USA | Automated & Manual | Ortem',
        description: 'US QA & software testing services. Automated testing, functional QA, performance & security testing, mobile app QA. Get a free audit.',
        keywords: 'QA testing services USA, software quality assurance company, automated testing services, mobile app testing company, performance testing USA, software QA outsourcing',
        canonical: 'https://ortemtech.com/services/qa-testing/',
        ogImage: OG_DEFAULT
    },
    'digital-marketing': {
        title: 'Digital Marketing USA | SEO, PPC & Content | Ortem',
        description: 'Data-driven digital marketing for US tech businesses. SEO, Google Ads, LinkedIn lead gen & conversion rate optimization. Grow your organic traffic today.',
        keywords: 'digital marketing services USA, SEO company USA, PPC management services, social media marketing company, content marketing USA, conversion rate optimization',
        canonical: 'https://ortemtech.com/services/digital-marketing/',
        ogImage: OG_DEFAULT
    },
    'custom-app-development': {
        title: 'Custom Software Development Services USA | Ortem',
        description: 'US custom software development company. Enterprise apps, SaaS platforms & startup MVPs — 1,000+ projects since 2012. Scalable, secure. Free project estimate.',
        keywords: 'custom software development services, custom software development company USA, custom software services, custom development services, enterprise software development company, bespoke software development, enterprise app development USA, SaaS development company, custom business software, CRM software development, ERP development',
        canonical: 'https://ortemtech.com/services/custom-app-development/',
        ogImage: OG_DEFAULT
    },
    'enterprise-mobility-solutions': {
        title: 'Enterprise Mobility Solutions USA | MDM & BYOD | Ortem',
        description: 'Enterprise mobility for US businesses. Secure MDM, BYOD management & field service apps that boost workforce productivity by 40%. Free assessment.',
        keywords: 'enterprise mobility solutions USA, MDM solutions, BYOD management software, field service app development, enterprise mobile app development USA, workforce mobility platform',
        canonical: 'https://ortemtech.com/services/enterprise-mobility-solutions/',
        ogImage: OG_DEFAULT
    },
    'cybersecurity': {
        title: 'Cybersecurity Services USA | HIPAA & SOC 2 | Ortem',
        description: 'Enterprise cybersecurity for US businesses. Penetration testing, API security, HIPAA/SOC 2/GDPR compliance & 24/7 threat monitoring. Free security audit.',
        keywords: 'cybersecurity services USA, penetration testing company, API security testing, HIPAA compliance software, SOC 2 compliance, cloud security services USA',
        canonical: 'https://ortemtech.com/services/cybersecurity/',
        ogImage: OG_SECURITY
    },

    // Industry Pages
    'healthcare-software-development': {
        title: 'Healthcare App Development Company USA | HIPAA | Ortem',
        description: 'US healthcare mobile app development company. HIPAA-compliant telemedicine, patient portals & EHR integrations with BAA included. Free consultation.',
        keywords: 'healthcare mobile app development company, healthcare app development services, healthcare app development company, healthcare software development USA, HIPAA compliant development, telemedicine app development, mHealth solutions, EHR integration',
        canonical: 'https://ortemtech.com/industries/healthcare-software-development/',
        ogImage: OG_HEALTHCARE
    },
    'logistics-software-development': {
        title: 'Logistics Software Development | Fleet & Supply Chain',
        description: 'Custom logistics software: fleet management, supply chain visibility & last-mile delivery apps. Reduce operational costs. Free consultation.',
        keywords: 'logistics software, supply chain management, fleet management software, last mile delivery app',
        canonical: 'https://ortemtech.com/industries/logistics-software-development/',
        ogImage: OG_LOGISTICS
    },
    'fintech-app-development': {
        title: 'Fintech Software Development Company USA | Banking | Ortem',
        description: 'US fintech software development company. PCI-DSS compliant mobile banking apps, digital wallets & trading platforms. Launch in 12–16 weeks. Free consultation.',
        keywords: 'fintech software development, fintech software development companies, fintech development companies, fintech app development, fintech app development companies, banking software development, digital wallet app, financial software development',
        canonical: 'https://ortemtech.com/industries/fintech-app-development/',
        ogImage: OG_FINTECH
    },
    'ecommerce-development': {
        title: 'eCommerce Development Company | Custom & Headless | Ortem',
        description: 'Custom eCommerce development. Headless commerce, B2B marketplaces & high-performance online stores. Scale your digital revenue. Free audit.',
        keywords: 'ecommerce development, headless commerce, shopify plus development, B2B ecommerce',
        canonical: 'https://ortemtech.com/industries/ecommerce-development/',
        ogImage: OG_ECOMMERCE
    },
    'education-technology': {
        title: 'EdTech Software Development | LMS & E-Learning | Ortem',
        description: 'Custom EdTech software: LMS platforms, e-learning apps & educational tools for schools and corporations. Free consultation.',
        keywords: 'edtech development, LMS development, e-learning app, smart classroom solutions',
        canonical: 'https://ortemtech.com/industries/education-technology/',
        ogImage: OG_DEFAULT
    },
    'real-estate-proptech-solutions': {
        title: 'Real Estate & PropTech Software Development | Ortem',
        description: 'Custom real estate software: property management platforms, MLS integrations & PropTech solutions for agents and brokers. Free consultation.',
        keywords: 'real estate software development, proptech solutions, property management app, MLS integration, real estate app development',
        canonical: 'https://ortemtech.com/industries/real-estate-proptech-solutions/',
        ogImage: OG_DEFAULT
    },
    'agriculture-software-development': {
        title: 'Agriculture Software Development | AgTech Solutions | Ortem',
        description: 'Custom AgTech software for precision farming, crop monitoring & supply chain management. Build smart agriculture apps. Free consultation.',
        keywords: 'agriculture software development, agtech solutions, precision farming app, crop monitoring software, farm management system',
        canonical: 'https://ortemtech.com/industries/agriculture-software-development/',
        ogImage: OG_DEFAULT
    },

    // Specific Service Pages
    'mvp-development': {
        title: 'MVP Development Company USA | 8–12 Week Delivery | Ortem',
        description: 'Ship your MVP in 8–12 weeks. Production-grade, investor-ready MVPs for startups & enterprises. No scope creep, no waste. Free scoping call.',
        keywords: 'MVP development company, minimum viable product development, startup MVP development, rapid MVP development USA, MVP app development',
        canonical: 'https://ortemtech.com/services/mvp-development/',
        ogImage: OG_DEFAULT,
    },
    'cloud-cost-optimisation': {
        title: 'Cloud Cost Optimisation | AWS & Azure FinOps | Ortem',
        description: 'Cut cloud costs by 30–50%. AWS, Azure & GCP FinOps audits, reserved instance planning & architecture right-sizing. Free cloud cost audit.',
        keywords: 'cloud cost optimisation, FinOps services, AWS cost optimisation, Azure cost management, GCP cost reduction, cloud spend audit',
        canonical: 'https://ortemtech.com/services/cloud-cost-optimisation/',
        ogImage: OG_DEFAULT,
    },
    'flutter-development': {
        title: 'Flutter Mobile App Development Company USA | Ortem',
        description: 'US Flutter app development. Ship iOS and Android from one codebase — faster than native. Pixel-perfect and App Store-ready. 100+ apps delivered.',
        keywords: 'Flutter mobile app development, Flutter app development company USA, Flutter developer USA, cross-platform app development, Flutter iOS Android app, Flutter development services, flutter app development company',
        canonical: 'https://ortemtech.com/services/flutter-development/',
        ogImage: OG_MOBILE,
    },
    'android-app-development': {
        title: 'Android App Development Company USA | Kotlin | Ortem',
        description: 'US Android app development company. Native Kotlin & Jetpack Compose apps for Google Play — enterprise-grade, HIPAA-ready, delivered in 8–16 weeks. Free quote.',
        keywords: 'android app development company USA, android app development agency, android app development services, native android app development, kotlin app development, android development company, android app development company in usa',
        canonical: 'https://ortemtech.com/services/android-app-development/',
        ogImage: OG_MOBILE,
    },
    'ios-app-development': {
        title: 'iOS App Development Company USA | Swift & SwiftUI | Ortem',
        description: 'US iOS app development company. Native Swift & SwiftUI iPhone and iPad apps — App Store-ready, delivered in 8–16 weeks. HealthKit, ARKit, Apple Pay. Free quote.',
        keywords: 'ios app development company USA, ios app development companies, ios app development services, ios development company, swift app development, iphone app development company, ios app development agency, native ios development',
        canonical: 'https://ortemtech.com/services/ios-app-development/',
        ogImage: OG_MOBILE,
    },
    'react-native-development': {
        title: 'React Native App Development USA | iOS & Android | Ortem',
        description: 'US React Native app development company. Cross-platform iOS & Android — native performance, shared codebase, faster iteration. 80+ apps shipped. Free quote.',
        keywords: 'React Native app development company USA, React Native developer USA, react native app development company, cross-platform app development React Native, React Native iOS Android, React Native development services',
        canonical: 'https://ortemtech.com/services/react-native-development/',
        ogImage: OG_MOBILE,
    },
    'native-app-development': {
        title: 'Native App Development Company USA | iOS & Android | Ortem',
        description: 'US native app development. Native iOS (Swift) and Android (Kotlin) — maximum performance, full platform API access, App Store and Play ready.',
        keywords: 'native app development, native application development, native mobile app development, native app development company, ios and android app development, native app developer, native mobile development',
        canonical: 'https://ortemtech.com/services/native-app-development/',
        ogImage: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&auto=format&fit=crop&q=80',
    },
    'cross-platform-app-development': {
        title: 'Cross-Platform App Development USA | Flutter & React Native',
        description: 'US cross-platform app development. One codebase for iOS and Android — Flutter and React Native specialists. MVPs in 8–12 weeks, 60% below local rates.',
        keywords: 'cross platform app development, cross platform mobile app development, cross platform app development company, cross-platform development services, flutter react native development, ios android app development, cross platform development company USA',
        canonical: 'https://ortemtech.com/services/cross-platform-app-development/',
        ogImage: OG_MOBILE,
    },
    'white-label-app-development': {
        title: 'White Label App Development USA | Rebrand & Launch | Ortem',
        description: 'US white label app development. Pre-built modules, custom branding, faster time-to-market. iOS and Android white label apps for agencies.',
        keywords: 'white label app development, white label mobile app development, white label app development company, white label mobile app, white label app builder, rebrand mobile app, white label software development',
        canonical: 'https://ortemtech.com/services/white-label-app-development/',
        ogImage: OG_MOBILE,
    },
    'ai-agent-development': {
        title: 'AI Agent Development Company USA | Custom Agentic AI | Ortem',
        description: 'US AI agent development company. Autonomous agents, multi-agent systems & LangGraph pipelines for enterprise production. 50+ AI deployments. Free scope call.',
        keywords: 'AI agent development, custom AI agents, agentic AI, autonomous AI agents, LangChain development, LangGraph, multi-agent systems',
        canonical: 'https://ortemtech.com/services/ai-agent-development/',
        ogImage: OG_AI,
    },
    'llm-integration': {
        title: 'LLM Integration Services USA | RAG Pipelines | Ortem',
        description: 'US LLM integration & RAG pipeline company. Add GPT-4, Claude or Llama to your product. HIPAA & GDPR-safe deployment. Free LLM consultation.',
        keywords: 'LLM integration services, RAG pipeline development, OpenAI integration, ChatGPT API integration, private LLM deployment, AI product development, llm integration',
        canonical: 'https://ortemtech.com/services/llm-integration/',
        ogImage: OG_AI,
    },
    'hipaa-compliant-development': {
        title: 'HIPAA-Compliant Software Development USA | Ortem',
        description: 'US HIPAA-compliant software development with BAA signing. Telemedicine, EHR integrations & patient portals. SOC 2-ready. Free healthcare tech consultation.',
        keywords: 'HIPAA compliant software development, healthcare app development, telemedicine app development, FHIR EHR integration, patient portal development, BAA signing',
        canonical: 'https://ortemtech.com/services/hipaa-compliant-development/',
        ogImage: OG_HEALTHCARE,
    },
    'nextjs-development': {
        title: 'Next.js Development Company | App Router Experts | Ortem',
        description: 'Expert Next.js development. Fast, SEO-optimised web apps & SaaS platforms with Next.js 14, TypeScript & Tailwind. US-managed, clear pricing.',
        keywords: 'Next.js development company, Next.js developer, Next.js 14 App Router, React Next.js development, Next.js SaaS development, hire Next.js developer',
        canonical: 'https://ortemtech.com/services/nextjs-development/',
        ogImage: OG_WEB,
    },
    'application-modernization': {
        title: 'Application Modernization Services | Legacy to Cloud | Ortem',
        description: 'Modernize legacy systems without downtime. Cloud migration, microservices, framework upgrades & data platform rebuilds. US-managed. Free assessment.',
        keywords: 'application modernization services, legacy system modernization, cloud migration services, microservices migration, re-platforming services USA, legacy software modernization',
        canonical: 'https://ortemtech.com/services/application-modernization/',
        ogImage: OG_CLOUD,
    },
    'data-engineering': {
        title: 'Data Engineering Services | Snowflake & BigQuery | Ortem',
        description: 'Expert data engineering: pipelines, Snowflake/BigQuery warehouses, real-time streaming & DataOps. US-managed team. Free architecture review.',
        keywords: 'data engineering services USA, data pipeline development, data warehouse design, Snowflake development, BigQuery data engineering, ETL ELT pipeline, DataOps services',
        canonical: 'https://ortemtech.com/services/data-engineering/',
        ogImage: OG_DATA,
    },
    // Targets the BI cluster deliberately (KD 0-7) rather than "data science"
    // terms, which /services/ai-ml-solutions/ already competes for.
    'data-analytics-bi': {
        title: 'Business Intelligence & Data Analytics Services | Ortem',
        description: 'BI consulting and data analytics services: executive dashboards, KPI definition, self-serve reporting and embedded analytics. US-managed. Free analytics review.',
        keywords: 'business intelligence service, business intelligence consultants, business intelligence consulting companies, data analytics services, data analytics consulting services, business analytics services, embedded analytics development',
        canonical: 'https://ortemtech.com/services/data-analytics-bi/',
        ogImage: OG_DATA,
    },
    'staff-augmentation': {
        title: 'Staff Augmentation Services | Developers in 5–7 Days | Ortem',
        description: 'Hire senior developers in 5–7 days. US-managed staff augmentation & dedicated dev teams for startups & enterprises. Flexible contracts. Free talent brief.',
        keywords: 'staff augmentation services, IT staff augmentation, staff augmentation company, dedicated development team, hire dedicated developers, remote development team, it outsourcing services, software developer augmentation',
        canonical: 'https://ortemtech.com/services/staff-augmentation/',
        ogImage: OG_DEFAULT,
    },
    'saas-development': {
        title: 'SaaS Development Company USA | Multi-Tenant & B2B | Ortem',
        description: 'US SaaS development company. Multi-tenant architecture, B2B SaaS, Stripe billing & self-serve onboarding. Launch to scale. Free SaaS scoping call.',
        keywords: 'SaaS development services, SaaS development company USA, SaaS application development, B2B SaaS development, SaaS product development, custom SaaS development, multi-tenant SaaS development, SaaS platform development',
        canonical: 'https://ortemtech.com/services/saas-development/',
        ogImage: OG_DEFAULT,
    },
    'dedicated-development-team': {
        title: 'Hire a Dedicated Development Team | Pods in 7–14 Days',
        description: 'Hire a dedicated development team: self-managed pod of senior engineers, QA & tech lead on a fixed monthly retainer. Assembled in 7–14 days. US-managed.',
        keywords: 'dedicated development team, hire dedicated developers, dedicated software development team, dedicated team model, offshore dedicated team, remote development team, dedicated developers cost',
        canonical: 'https://ortemtech.com/services/dedicated-development-team/',
        ogImage: OG_DEFAULT,
    },
    'outsource-software-development': {
        title: 'Outsource Software Development | US-Managed Company | Ortem',
        description: 'Outsource software development to a US-managed company. Senior engineers at 40–60% below US agency rates, fixed milestone pricing & full IP assignment.',
        keywords: 'outsource software development, software development outsourcing company, outsource software development company USA, software outsourcing services, IT outsourcing company, outsourced development team',
        canonical: 'https://ortemtech.com/services/outsource-software-development/',
        ogImage: OG_DEFAULT,
    },
    'enterprise-software-development': {
        title: 'Enterprise Software Development Company | Ortem Technologies',
        description: 'Enterprise software development: custom platforms, workflow automation, ERP/CRM integration and modernization with SSO and audit trails built in.',
        keywords: 'enterprise software development company, enterprise application development, custom enterprise software, enterprise software solutions, workflow automation software, system integration services',
        canonical: 'https://ortemtech.com/services/enterprise-software-development/',
        ogImage: OG_DEFAULT,
    },
    'telehealth-app-development': {
        title: 'Telehealth App Development Company | HIPAA-Compliant | Ortem',
        description: 'Telehealth app development: HIPAA-compliant video visits, scheduling, e-prescribing & EHR integration (Epic, Cerner). Built by a healthcare software team.',
        keywords: 'telehealth app development, telemedicine app development company, telehealth software development, HIPAA compliant telehealth app, telemedicine platform development, virtual care platform development',
        canonical: 'https://ortemtech.com/services/telehealth-app-development/',
        ogImage: OG_DEFAULT,
    },
    'hire-ai-developers': {
        title: 'Hire AI Developers | Production LLM Engineers in 5–10 Days',
        description: 'Hire AI developers with production LLM experience: RAG pipelines, AI agents, fine-tuning & MLOps. Pre-vetted, placed in 5–10 days, US-managed contracts.',
        keywords: 'hire AI developers, hire AI engineers, hire LLM developers, hire machine learning engineers, AI developer staffing, hire RAG developers, hire AI agent developers',
        canonical: 'https://ortemtech.com/services/hire-ai-developers/',
        ogImage: OG_DEFAULT,
    },

    // Regional Pages
    'regions-australia': {
        title: 'Software Development Company Australia | Ortem Technologies',
        description: 'Custom software, AI & mobile development for Australian businesses. AEST-aligned engineering, fintech, agtech & proptech expertise. Start a conversation.',
        keywords: 'software development company Australia, custom software development Sydney, mobile app development Melbourne, software development Brisbane, AI development Australia',
        canonical: 'https://ortemtech.com/regions/australia/',
        ogImage: OG_DEFAULT,
    },
    'regions-uk': {
        title: 'Software Development Company UK | Ortem Technologies',
        description: 'GDPR-compliant custom software, AI & mobile development for UK businesses. UK-timezone-aligned. Fintech, healthcare & enterprise expertise.',
        keywords: 'software development company UK, custom software development London, mobile app development UK, software development Manchester, AI development UK, GDPR compliant software UK',
        canonical: 'https://ortemtech.com/regions/uk/',
        ogImage: OG_DEFAULT,
    },
    'regions-middle-east': {
        title: 'Software Development Company UAE & Middle East | Ortem',
        description: 'Custom software, AI & mobile for UAE, Saudi Arabia, Qatar & Gulf businesses. US-managed, enterprise-grade delivery since 2012.',
        keywords: 'software development company UAE, software development Dubai, custom software Saudi Arabia, mobile app development Middle East, AI development Gulf, tech company Middle East',
        canonical: 'https://ortemtech.com/regions/middle-east/',
        ogImage: OG_DEFAULT,
    },

    // Company Pages
    'why-ortem': {
        title: 'Ortem vs LeewayHertz, Appinventiv & More — Agency Comparison',
        description: 'Ortem vs LeewayHertz, Appinventiv, Simform & SoluLab — 2026 AI development agency comparison. Pricing, delivery speed, AI capabilities & client outcomes.',
        keywords: 'LeewayHertz alternatives, Appinventiv alternatives, Simform alternatives, SoluLab alternatives, Hyperlink InfoSystem alternatives, 10Pearls alternatives, Intellectsoft alternatives, Ortem Technologies vs LeewayHertz, best AI development company 2026, AI development agency comparison 2026',
        canonical: 'https://ortemtech.com/why-ortem/',
        ogImage: OG_AI,
    },
    'our-methodology': {
        title: 'The BridgeMethod™ | Our Development Methodology | Ortem',
        description: 'The BridgeMethod™: Ortem\'s proprietary delivery framework. Discover, Architect, Bridge, Sprint, Quality Gate, Measure. Software that ships on time.',
        keywords: 'software development methodology, agile development process, software delivery framework, Ortem Technologies methodology, BridgeMethod',
        canonical: 'https://ortemtech.com/our-methodology/',
        ogImage: OG_DEFAULT,
    },
    'delivery-model': {
        title: 'How We Deliver Software | Ortem Technologies Delivery Model',
        description: 'Sprint-based delivery with US-managed accountability, dedicated engineering pods, full IP ownership, 98% client retention. 1,000+ projects since 2012.',
        keywords: 'software delivery model, US managed software development, dedicated development team, agile software delivery, software project management, custom software development process',
        canonical: 'https://ortemtech.com/about-our-delivery-model/',
        ogImage: OG_DEFAULT,
    },
    'compliance': {
        title: 'GDPR & HIPAA Compliant Software Development | Ortem',
        description: 'Ortem Technologies is GDPR & HIPAA compliant. BAAs for healthcare clients, DPAs for EU clients, rigorous data security across all engagements.',
        keywords: 'HIPAA compliant software development, GDPR compliant development company, healthcare software compliance, data security software company, SOC 2 ready development',
        canonical: 'https://ortemtech.com/compliance/',
        ogImage: OG_DEFAULT,
    },
    'innovation-lab': {
        title: 'Innovation Lab | Emerging Tech Research | Ortem Technologies',
        description: 'The Ortem Innovation Lab: agentic AI research, edge computing prototypes, spatial computing & developer tooling. Where we explore what is next.',
        keywords: 'AI innovation lab, software research lab, agentic AI research, edge AI, spatial computing, developer tools',
        canonical: 'https://ortemtech.com/innovation-lab/',
        ogImage: OG_DEFAULT,
    },
    'app-cost-calculator': {
        title: 'App Development Cost Calculator 2026 | Ortem Technologies',
        description: 'Free app development cost calculator. Get a realistic estimate in 60 seconds for mobile apps, web apps, SaaS or AI products. Used by 500+ founders & PMs.',
        keywords: 'app development cost calculator, software development cost estimator, mobile app cost, web app development cost, how much does an app cost',
        canonical: 'https://ortemtech.com/tools/app-cost-calculator/',
        ogImage: OG_DEFAULT,
    },

    // Legal
    privacyPolicy: {
        title: "Privacy Policy | Ortem Technologies",
        description: "Read the Privacy Policy of Ortem Technologies to understand how we collect, use, and protect your personal information.",
        canonical: "https://ortemtech.com/privacy/",
        noindex: true,
    },
    termsOfService: {
        title: "Terms of Service | Ortem Technologies",
        description: "Read the Terms of Service for Ortem Technologies. Understand your rights and responsibilities when using our services.",
        canonical: "https://ortemtech.com/terms/",
        noindex: true,
    }
};
